import { useEffect, useMemo, useState } from "react";
import {
  Boxes,
  Bell,
  X,
  Wifi,
  WifiOff,
  Zap,
  Share,
  Plus,
  ChevronRight,
  Download,
} from "lucide-react";
import {
  isStandalone,
  useDeferredInstallPrompt,
} from "@/hooks/use-pwa-install";
import { useTranslation } from "react-i18next";

const SEEN_KEY = "pwa-mobile-install-prompt-dismissed-until";
const INLINE_DISMISS_KEY = "pwa-mobile-install-inline-dismissed-until";
const SHOW_AFTER_MS = 4000;
const DISMISS_DAYS = 14;

function isFutureTimestamp(raw: string | null): boolean {
  if (!raw) return false;
  const until = parseInt(raw, 10);
  return Number.isFinite(until) && Date.now() < until;
}

function markDismissed(key: string, days = DISMISS_DAYS) {
  try {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    window.localStorage.setItem(key, String(until));
  } catch {}
}

function detectPlatform(): "android" | "ios" | "other" {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
  if (!isIOS) return false;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isSafari;
}

/**
 * Full-screen modal install prompt — Android bottom sheet or iOS overlay.
 * Shows once per first session (after a short delay), respects dismissal.
 */
export function MobileInstallModal() {
  const platform = useMemo(detectPlatform, []);
  const { canInstall, prompt } = useDeferredInstallPrompt();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    if (isFutureTimestamp(window.localStorage.getItem(SEEN_KEY))) return;

    const showableNow = platform === "android" ? canInstall : isIOSSafari();
    if (!showableNow) return;

    const t = setTimeout(() => setOpen(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [platform, canInstall]);

  const close = () => {
    markDismissed(SEEN_KEY);
    setOpen(false);
  };

  const install = async () => {
    const outcome = await prompt();
    if (outcome === "accepted" || outcome === "dismissed") {
      markDismissed(SEEN_KEY);
    }
    setOpen(false);
  };

  if (!open) return null;

  if (platform === "android") {
    return <AndroidSheet onClose={close} onInstall={install} />;
  }
  if (platform === "ios") {
    return <IOSOverlay onClose={close} />;
  }
  return null;
}

function AndroidSheet({
  onClose,
  onInstall,
}: {
  onClose: () => void;
  onInstall: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end"
      data-testid="modal-pwa-install-android"
    >
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
      />
      <div className="relative w-full bg-white rounded-t-3xl shadow-2xl pb-6 animate-in slide-in-from-bottom">
        <div className="flex justify-center pt-2">
          <div className="w-9 h-1 rounded-full bg-slate-200" />
        </div>

        <div className="px-5 pt-3 pb-1 flex items-start justify-between">
          <p className="text-[11px] text-slate-500 font-medium">
            {window.location.host}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="-me-1 -mt-1 w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"
            data-testid="button-install-close"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="px-5 pt-4 flex items-center gap-3.5">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md shadow-orange-500/30">
            <Boxes className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-semibold text-slate-900 leading-tight">
              {t("seg20.installInfrastock")}
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">{t("seg20.categoryProductivity")}</p>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[10px] text-amber-500 tracking-widest">
                ★★★★★
              </span>
              <span className="text-[10px] text-slate-400">· {t("seg20.workOffline")}</span>
            </div>
          </div>
        </div>

        <div className="px-5 pt-4">
          <p className="text-[12px] text-slate-600 leading-relaxed">
            {t("seg20.androidBlurb")}
          </p>
        </div>

        <div className="mx-5 mt-4 grid grid-cols-3 gap-2">
          <Perk icon={<Zap className="w-4 h-4" />} label={t("seg20.perkFaster")} />
          <Perk icon={<WifiOff className="w-4 h-4" />} label={t("seg20.perkOffline")} />
          <Perk icon={<Bell className="w-4 h-4" />} label={t("seg20.perkPushAlerts")} />
        </div>

        <div className="px-5 pt-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-slate-200 text-[13px] font-medium text-slate-700 active:bg-slate-100"
            data-testid="button-install-not-now"
          >
            {t("seg20.notNow")}
          </button>
          <button
            type="button"
            onClick={onInstall}
            className="flex-[1.4] h-11 rounded-xl bg-slate-900 text-white text-[13px] font-semibold active:bg-slate-800 flex items-center justify-center gap-1.5"
            data-testid="button-install-confirm"
          >
            <Download className="w-4 h-4" />
            {t("seg20.install")}
          </button>
        </div>
      </div>
    </div>
  );
}

function IOSOverlay({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[60]"
      data-testid="modal-pwa-install-ios"
    >
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
      />

      <div className="absolute start-3 end-3 top-16 bg-white rounded-3xl shadow-2xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <Boxes className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-slate-900 leading-tight">
                {t("seg20.addToHomeScreenTitle")}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {t("seg20.addToHomeScreenSubtitle")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-me-1 w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center shrink-0"
            data-testid="button-install-close"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="space-y-2.5 mt-4">
          <Step
            num="1"
            text={
              <>
                {t("seg20.iosStep1Pre")}{" "}
                <Inline icon={<Share className="w-3.5 h-3.5" />} label={t("seg20.shareLabel")} />{" "}
                {t("seg20.iosStep1Post")}
              </>
            }
          />
          <Step
            num="2"
            text={
              <>
                {t("seg20.iosStep2Pre")}{" "}
                <Inline
                  icon={<Plus className="w-3.5 h-3.5" />}
                  label={t("seg20.addToHomeScreenLabel")}
                />
              </>
            }
          />
          <Step
            num="3"
            text={t("seg20.iosStep3")}
          />
        </div>

        <div className="mt-4 rounded-2xl bg-slate-900 p-3 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shrink-0">
            <Boxes className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-white">Infrastock</p>
            <p className="text-[10px] text-slate-400 truncate">
              {window.location.host}
            </p>
          </div>
        </div>

        <p className="text-[10px] text-slate-400 mt-3 text-center">
          {t("seg20.iosNoAutoPrompt")}
        </p>
      </div>

      <div className="absolute left-1/2 -translate-x-1/2 bottom-24 flex flex-col items-center pointer-events-none">
        <div className="bg-white text-slate-900 text-[11px] font-semibold px-3 py-1.5 rounded-full shadow-lg mb-1 animate-bounce">
          {t("seg20.tapShareBelow")}
        </div>
        <svg
          width="22"
          height="32"
          viewBox="0 0 22 32"
          fill="none"
          className="text-white drop-shadow-lg"
        >
          <path
            d="M11 0v26m0 0l-9-9m9 9l9-9"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

function Perk({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 px-2 py-2.5 flex flex-col items-center gap-1">
      <span className="text-slate-700">{icon}</span>
      <span className="text-[10px] font-medium text-slate-600">{label}</span>
    </div>
  );
}

function Step({ num, text }: { num: string; text: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
        {num}
      </div>
      <p className="text-[13px] text-slate-700 leading-snug flex-1">{text}</p>
    </div>
  );
}

function Inline({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1 align-middle bg-slate-100 text-slate-800 rounded-md px-1.5 py-0.5 text-[12px] font-medium">
      <span className="text-blue-500">{icon}</span>
      {label}
    </span>
  );
}

/**
 * Dismissible inline banner that lives at the top of MobileHome's body
 * (above the Actions grid). Always shown if not installed and not dismissed
 * — acts as the fallback nudge for users who skipped the modal.
 */
export function MobileInstallInlineBanner() {
  const { t } = useTranslation();
  const platform = useMemo(detectPlatform, []);
  const { canInstall, prompt } = useDeferredInstallPrompt();
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) {
      setHidden(true);
      return;
    }
    if (isFutureTimestamp(window.localStorage.getItem(INLINE_DISMISS_KEY))) {
      setHidden(true);
      return;
    }
    if (platform === "android" || platform === "ios") {
      setHidden(false);
    }
  }, [platform]);

  if (hidden) return null;

  const onPrimary = async () => {
    if (platform === "android" && canInstall) {
      const outcome = await prompt();
      if (outcome === "accepted") {
        markDismissed(INLINE_DISMISS_KEY, 365);
        setHidden(true);
        return;
      }
    }
    if (platform === "ios") {
      // Surface the iOS instructions overlay again by clearing modal-dismiss
      try {
        window.localStorage.removeItem(SEEN_KEY);
      } catch {}
      window.dispatchEvent(new CustomEvent("pwa-show-ios-instructions"));
    }
  };

  const onDismiss = () => {
    markDismissed(INLINE_DISMISS_KEY);
    setHidden(true);
  };

  const primaryLabel = platform === "ios" ? t("seg20.howTo") : t("seg20.install");

  return (
    <div className="px-4 pt-4" data-testid="banner-pwa-install-inline">
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/20">
        <div className="absolute -end-4 -top-4 w-24 h-24 rounded-full bg-white/10" />
        <div className="absolute -end-10 bottom-0 w-20 h-20 rounded-full bg-white/5" />

        <div className="relative p-3.5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
            <Download className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold leading-tight">
              {t("seg20.installOnThisPhone")}
            </p>
            <p className="text-[11px] text-white/80 mt-0.5">
              {t("seg20.installBenefits")}
            </p>
          </div>
          <button
            type="button"
            onClick={onPrimary}
            className="bg-white text-orange-600 text-[12px] font-bold px-3 py-1.5 rounded-lg active:scale-95 shrink-0"
            data-testid="button-install-inline-primary"
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t("seg20.dismiss")}
            className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white/80 shrink-0"
            data-testid="button-install-inline-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
